import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    IconButton,
    TextField,
    Tooltip,
    MenuItem,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    DeleteOutline as DeleteIcon,
    CloudUpload as UploadIcon,
} from '@material-ui/icons';
import useStyles from '../../../../assets/styles/ManageClassesStyles';
import { toast } from '../../../../utils/utils';
import { TOAST_TYPES } from '../../../../utils/constants';
import {
    validateClassListForCreateApi,
    MAX_SECTION_NAME_LEN,
    MAX_SECTION_LEVEL_LEN,
    MAX_PERSON_NAME_LEN,
    MAX_EMAIL_LEN,
} from '../../../../utils/classCreateValidation';
import { checkUsernamePrefixService } from '../../../../services/sections/sections.service';
import { getSectionStudentsService, updateSectionStudentService } from '../../../../services/client.service';
import { getCountries, getStatesByCountry, useGeo } from '../../../../utils/geo';

/**
 * Matches .studentsInput's own flat, borderless look (see
 * ManageClassesStyles.js) — no boxed outline, just the row's own right-hand
 * divider — so the Country/State Autocomplete fields sit flush with the
 * plain <select>/<input> cells around them instead of showing MUI's own
 * outlined box on top of the table's own borders.
 */
const autocompleteFieldSx = {
    '& .MuiOutlinedInput-root': {
        padding: '0 !important',
        borderRadius: 0,
        backgroundColor: 'transparent',
        '& fieldset': { border: 'none' },
        borderRight: '1px solid var(--brand-border, #f0f0f0)',
        '&.Mui-focused': {
            backgroundColor: 'rgba(0, 151, 178, 0.04)',
            boxShadow: 'inset 0 0 0 2px #0097b2',
            borderRightColor: 'transparent',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '12px 16px !important',
        fontSize: '15px',
        fontWeight: 500,
        color: 'var(--brand-text, #333)',
    },
};

/** New-school form fields — mirror the CSV school block columns
 *  (Head Teacher/Group Email, First, Last, Country, City, Postal, School/Group Name). */
const EMPTY_SCHOOL_FORM = {
    schoolName: '',
    headEmail: '',
    headFirstName: '',
    headLastName: '',
    headCountry: '',
    headCity: '',
    headPostalCode: '',
};

const hasUncheckedUsernamePrefix = (tabs) =>
    (tabs || []).some((c) => {
        const p = String(c.userPrefix || '').trim();
        return p && (!c.userPrefixValid || c.userPrefixChecked !== p);
    });

const runSave = (classList, onSave, saveDisabled, prefixError, { enrollOnly = false } = {}) => {
    if (saveDisabled) {
        toast(TOAST_TYPES.INFO, 'Classes', 'Please wait for the current request to finish.');
        return;
    }
    if (prefixError) {
        toast(TOAST_TYPES.WARN, 'Classes', prefixError);
        return;
    }
    if (hasUncheckedUsernamePrefix(classList)) {
        toast(TOAST_TYPES.WARN, 'Classes', 'Click outside the Username Prefix field to verify it is available before saving.');
        return;
    }
    // Anything the form is ALREADY flagging in red blocks the save. Without this
    // a field showing "Too short" / "Letters only" / "Invalid email" was still
    // accepted and the class was created with it.
    const fieldError = collectInlineFieldErrors(classList);
    if (fieldError) {
        toast(TOAST_TYPES.WARN, 'Classes', fieldError);
        return;
    }
    const v = validateClassListForCreateApi(classList);
    if (!v.ok) {
        toast(TOAST_TYPES.WARN, 'Classes', v.message);
        return;
    }
    // Enroll saves without a learner email pass through: the page handler
    // persists edited class details (username prefix / postal code) in that
    // case, and warns only when nothing changed either.
    Promise.resolve(onSave(classList)).catch(() => {});
};

const EMPTY_STUDENT = {
    firstName: '', lastName: '', studentEmail: '', yearOfBirth: '', parentEmail: '',
    gender: '', city: '', country: '', state: '', postalCode: '',
};

// ── Inline (as-you-type) field validation ────────────────────────────────────
// These run on every keystroke and only ever DECORATE the field — they never
// block typing and never gate submit on their own, so a partially typed value
// can't trap the user. Submit keeps its own checks.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Letters (incl. accents), spaces, apostrophes and hyphens — no digits/symbols.
const NAME_RE = /^[\p{L}][\p{L}\s'’.-]*$/u;

/** Error text for a person's name, or '' when fine. Empty is NOT an error here —
 *  a half-filled row is normal while typing; submit enforces required-ness. */
const nameError = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    if (v.length < 2) return 'Too short';
    if (!NAME_RE.test(v)) return 'Letters only';
    return '';
};

/** Error text for an email, or '' when fine. Empty is not an error while typing. */
const emailError = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    return EMAIL_RE.test(v) ? '' : 'Invalid email';
};

/**
 * The parent's address, unlike the learner's, is not optional.
 *
 * Every consent pathway mails the parent, and publishing a class now refuses
 * outright when a learner has no parent address — so accepting a blank here only
 * defers the same rejection to a later, more expensive moment, after the roster
 * is saved and someone is trying to go live. The learner's own address stays
 * optional: a child with no email of their own is a supported case.
 */
const requiredEmailError = (value) => {
    const v = String(value || '').trim();
    if (!v) return 'Required';
    return EMAIL_RE.test(v) ? '' : 'Invalid email';
};

/**
 * Has the user put anything in this row at all?
 *
 * Shared by the validator and the header counts. The payload builder already
 * skips rows with no address, so a wholly blank row is a placeholder, not an
 * incomplete learner — validating it would fire "Parent Email required" on every
 * empty row the moment the table is widened.
 */
const isStudentRowFilled = (s) => !!(s.firstName || s.lastName || s.studentEmail || s.yearOfBirth || s.parentEmail);

/** Red outline + tooltip for an input that currently fails validation. */
const invalidInputSx = (hasError) => (hasError
    ? { borderColor: '#DC2626', outlineColor: '#DC2626', backgroundColor: 'rgba(220,38,38,0.04)' }
    : undefined);

/**
 * The first inline error currently visible on the form, as a user-facing
 * message — or '' when every field the user can see is clean.
 *
 * The red outlines above used to be decoration ONLY: submit ran just
 * validateClassListForCreateApi, which checks maximum lengths plus the teacher
 * email format and nothing else. So a name showing "Too short" or "Letters
 * only", and any bad learner row (learner fields were not checked at all),
 * saved anyway. This gates submit on exactly the rules the user can already see
 * failing — no NEW rules are introduced, so a form that looks clean still saves.
 */
const collectInlineFieldErrors = (classList) => {
    for (const c of classList || []) {
        const className = String(c.className || '').trim();
        // Same rule validateClassListForCreateApi uses: an unnamed tab is ignored.
        if (!className) continue;
        const where = `Class "${className}"`;

        for (const [label, err] of [
            ['Teacher First Name', nameError(c.teacherFirstName)],
            ['Teacher Last Name',  nameError(c.teacherLastName)],
            ['Teacher Email',      emailError(c.teacherEmail)],
        ]) {
            if (err) return `${where}: ${label} — ${err}. Fix the highlighted field before saving.`;
        }

        // CSV mode's learners come from the uploaded file, not this table, so the
        // placeholder row behind that tab must not be validated.
        if (c.inputMode !== 'table') continue;
        const rows = c.students || [];
        for (let i = 0; i < rows.length; i++) {
            if (!isStudentRowFilled(rows[i])) continue;
            for (const [label, err] of [
                ['First Name',    nameError(rows[i].firstName)],
                ['Last Name',     nameError(rows[i].lastName)],
                ['Learner Email', emailError(rows[i].studentEmail)],
                ['Parent Email',  requiredEmailError(rows[i].parentEmail)],
            ]) {
                if (err) return `${where}, learner row ${i + 1}: ${label} — ${err}. Fix the highlighted field before saving.`;
            }
        }
    }
    return '';
};

// Same year range as the my-dq demographic form (Demographic.page.jsx):
// last 99 years, newest = current year - 8 (minimum learner age is 8).
const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEAR_OPTIONS = Array.from({ length: 99 }, (_, i) => CURRENT_YEAR - i - 8);

const GRADE_LEVEL_OPTIONS = ['Kindergarten', ...Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`)];

// School-year style range (e.g. "2025-2026") — a few years back through a
// couple ahead, same idea as BIRTH_YEAR_OPTIONS's rolling window.
const ACADEMIC_YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => {
    const start = CURRENT_YEAR - 5 + i;
    return `${start}-${start + 1}`;
});

const buildStudentsArray = (count, existing = []) => {
    const n = Math.max(1, parseInt(count) || 1);
    return Array.from({ length: n }, (_, i) => existing[i] ? { ...existing[i] } : { ...EMPTY_STUDENT });
};

const createEmptyClass = () => ({
    id: Date.now() + Math.random(),
    className: '',
    gradeLevel: '',
    academicYear: '',
    userPrefix: '',
    userPrefixValid: false,
    userPrefixChecked: '',
    // Enroll mode: usernames continue AFTER the class's existing students
    // (prefix<maxN+1>…). Restarting at 1 would regenerate an existing username,
    // which the CSV service treats as the SAME student (same-org reuse) and
    // silently overwrites them.
    usernameStartOffset: 0,
    // Usernames already present in this class's roster (enroll mode). New
    // usernames are generated to skip these, so an existing learner is never
    // regenerated — which the CSV service would treat as a same-user update
    // (reported success, but no new student added).
    existingUsernames: [],
    studentsCount: '',
    teacherFirstName: '',
    teacherLastName: '',
    teacherEmail: '',
    // Add student details: "now" shows the roster table below; "later"
    // hides it — the class is created with no students, added anytime from
    // its own page. Purely a UI choice — "later" is functionally identical
    // to studentsCount staying 0.
    studentEntryMode: 'now',
    schoolPostalCode: '',
    students: [{ ...EMPTY_STUDENT }],
    inputMode: 'table',
    csvFile: null,
});

const splitInchargeName = (fullName) => {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { first: '', last: '' };
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
};

const buildClassFromEnrollTarget = (target) => {
    const { first, last } = splitInchargeName(target?.teacherName);
    const grade = String(target?.gradeLevel || '').trim();
    return {
        ...createEmptyClass(),
        className: String(target?.className || '').trim(),
        gradeLevel: grade === '—' ? '' : grade,
        teacherFirstName: String(target?.teacherFirstName || first).trim(),
        teacherLastName: String(target?.teacherLastName || last).trim(),
        teacherEmail: String(target?.teacherEmail || '').trim(),
    };
};

// Existing Students tab: the stored values read the way the Add Students table
// entered them. Gender is stored lowercase, country as its ISO code, and state
// as its code when it was picked from the list (CSV uploads may hold a name).
const genderLabel = (g) => (g ? g.charAt(0).toUpperCase() + g.slice(1) : '');
const countryLabel = (code) => getCountries().find((c) => c.code === code)?.label || code || '';
const stateLabel = (country, state) =>
    getStatesByCountry(country).find((s) => s.code === state)?.name || state || '';

/** Existing Students tab: rows per page (server-side pagination — the roster
 * can hold thousands of learners, so only one page travels per request). */
const EXISTING_PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_EXISTING_PAGE_SIZE = 10;

/** Previous/Next pager buttons: brand orange, and ALWAYS the same orange — the
 * disabled state is just a faded version of the same color (opacity only), so
 * stepping a page never changes the OTHER button's color, only its brightness. */
const paginationNavBtnSx = {
    textTransform: 'none',
    fontSize: '12.5px',
    color: '#FF6900',
    borderColor: '#FF6900',
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255,105,0,0.08)' },
    '&.Mui-disabled': { color: '#FF6900', borderColor: '#FF6900', opacity: 0.4 },
};

/** Roster sorted newest-first — the teacher may have changed prefix/postal on a
 * later batch, so the most recently created student carries the values to
 * continue with (the roster endpoint itself sorts alphabetically). */
const sortNewestFirst = (students) =>
    [...(students || [])].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
    );

/**
 * Derive the class's username pattern from its roster. When the class has a
 * PERSISTED prefix (preferredPrefix, stored on the section), that prefix wins
 * and only its numbering is counted; otherwise the prefix of the NEWEST
 * student's username is used (legacy classes saved before persistence existed).
 * maxN = highest trailing number for the chosen prefix (peace1, peace7 → 7).
 */
const deriveUsernamePattern = (students, preferredPrefix = '') => {
    let prefix = String(preferredPrefix || '').trim();
    let maxN = 0;
    for (const s of sortNewestFirst(students)) {
        const m = String(s.username || '').trim().match(/^(.*?)(\d+)$/);
        if (!m || !m[1]) continue;
        if (!prefix) prefix = m[1]; // newest matching student wins
        if (m[1].toLowerCase() === prefix.toLowerCase()) {
            maxN = Math.max(maxN, parseInt(m[2], 10) || 0);
        }
    }
    return { prefix, maxN };
};

const ENROLL_LINK_BUCKETS = [
    { key: 'section_exclusive', label: 'This class only' },
    { key: 'multi_section', label: 'Shared across classes' },
    { key: 'org_wide', label: 'Org-wide' },
];

const formatModuleType = (moduleType) => {
    const u = String(moduleType || '').toUpperCase().replace(/\s+/g, '_');
    if (u === 'LEARNING_PROGRAM') return 'Learning program';
    if (u === 'ASSESSMENT' || u === 'STANDARD_ASSESSMENT') return 'Assessment';
    return 'Module';
};

const AddClassStudentsDialog = ({
    open,
    onClose,
    onSave,
    saveDisabled,
    uploadProgress = null, // { done, total } while a batched CSV upload runs
    onLicenseCsvUpload,
    onDownloadLicenseTemplate,
    mode = 'create',
    enrollTarget = null,
    linkedData = null,
    linkedLoading = false,
    orgCode = null,
    schools = [], // org's saved schools (INSTITUTE rows) for the School picker
    // A School Network has no single "workspace" org to create a class in —
    // each class belongs to one of the network's own member schools. When
    // this is non-empty (create mode, network viewer only) a "Which school?"
    // picker renders and is required before Create Class is enabled.
    networkSchools = [], // [{ org_code, org_name }]
    defaultNetworkOrgCode = '', // pre-select the school already scoped in the page
}) => {
    useGeo(); // re-render with the API's lists once they load
    const isEnrollMode = mode === 'enroll';
    const classes = useStyles();
    const [classList, setClassList] = useState([]);
    const [activeTabId, setActiveTabId] = useState(null);
    const fileInputRef = useRef(null);
    const [prefixChecking, setPrefixChecking] = useState(false);
    const [prefixError, setPrefixError] = useState('');

    // ── Residue of the removed School / Group picker ───────────────────────
    // The picker is gone (see the note where it used to render), but these three
    // still feed resolvePrefillSchool, which ENROLL mode reads to show the school
    // a class already belongs to. In create mode they now stay at their initial
    // values, so that function correctly resolves to "no school".
    const [schoolMode, setSchoolMode] = useState('existing');
    const [selectedSchoolCode, setSelectedSchoolCode] = useState('');
    const [newSchool, setNewSchool] = useState({ ...EMPTY_SCHOOL_FORM });

    // ── Network "Which school?" picker (create mode only) ──────────────────
    const [selectedNetworkOrgCode, setSelectedNetworkOrgCode] = useState('');

    // ── Existing Students tab (enroll mode, scoped to this class) ──────────
    const enrollSectionCode = String(enrollTarget?.sectionCode || '').trim();
    const [viewMode, setViewMode] = useState('form'); // 'form' | 'existing'
    const [existingStudents, setExistingStudents] = useState([]); // current PAGE
    const [existingLoading, setExistingLoading] = useState(false);
    const [existingLoaded, setExistingLoaded] = useState(false);
    const [existingError, setExistingError] = useState('');
    const [existingSearch, setExistingSearch] = useState('');
    const [existingPage, setExistingPage] = useState(1);

    // ── Inline edit of one existing learner ────────────────────────────────
    // Keyed by username rather than row index: the roster is paginated and
    // searched server-side, so an index means nothing across a refetch.
    const [editingUsername, setEditingUsername] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [existingPageSize, setExistingPageSize] = useState(DEFAULT_EXISTING_PAGE_SIZE);
    const [existingTotal, setExistingTotal] = useState(0); // filtered total (all pages)
    // Whole-section derivation source: { usernames (newest first), latestPostal,
    // students? (legacy full list) } — independent of the paginated table.
    const [rosterMeta, setRosterMeta] = useState(null);
    // Username prefix / numbering offset / postal code recovered from the
    // class's roster (enroll mode) — {prefix, maxN, postal} or null.
    const [enrollDerived, setEnrollDerived] = useState(null);

    useEffect(() => {
        if (!open) return;
        const initial = isEnrollMode && enrollTarget
            ? buildClassFromEnrollTarget(enrollTarget)
            : createEmptyClass();
        setClassList([initial]);
        setActiveTabId(initial.id);
        setPrefixError('');
        // Reset the existing-students tab each time the dialog opens.
        setViewMode('form');
        setExistingStudents([]);
        setExistingLoaded(false);
        setExistingError('');
        setExistingSearch('');
        setExistingPage(1);
        setExistingPageSize(DEFAULT_EXISTING_PAGE_SIZE);
        setExistingTotal(0);
        setRosterMeta(null);
        setEnrollDerived(null);
        // School picker: enroll mode preselects the class's current school;
        // create mode defaults to Existing when the org already has schools,
        // otherwise straight to the New School form (first-ever class).
        const currentCode = String(enrollTarget?.instituteCode || '').trim();
        const hasCurrent = currentCode && currentCode !== 'default'
            && (schools || []).some((s) => s.institute_code === currentCode);
        if (isEnrollMode) {
            setSchoolMode('existing');
            setSelectedSchoolCode(hasCurrent ? currentCode : '');
        } else {
            setSchoolMode((schools || []).length ? 'existing' : 'new');
            setSelectedSchoolCode('');
        }
        setNewSchool({ ...EMPTY_SCHOOL_FORM });
        setSelectedNetworkOrgCode(String(defaultNetworkOrgCode || '').trim());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEnrollMode, enrollTarget]);

    /**
     * Resolve the School selection for save.
     *
     * There is no School selection to make any more. Add a Class never asks
     * which school a class belongs to — see the note where the picker used to
     * render, below. A class belongs to the workspace it is created in.
     *
     * Returns { school, error }:
     *   create mode → school: null (nothing to choose; the workspace is the school)
     *   enroll mode → school: null (the class keeps the school it already has)
     *
     * Kept as a function rather than inlined so the two call sites, the CSV path
     * and everything downstream that reads `school` keep working unchanged — the
     * answer is simply always "none" now.
     */
    const buildSchoolSelection = () => ({ school: null, error: null });

    // pageSize travels as a param (not just state) so a fresh "Rows per page"
    // selection can reload immediately without reading a stale closure value.
    const startEditStudent = (student) => {
        setEditError('');
        setEditingUsername(student.username);
        setEditDraft({
            firstName: student.firstName || '',
            lastName: student.lastName || '',
            studentEmail: student.studentEmail || '',
            email: student.email || '',
            yearOfBirth: student.yearOfBirth || '',
            gender: genderLabel(student.gender),
            city: student.city || '',
            country: student.country || '',
            state: student.state || '',
            postalCode: student.postalCode || '',
        });
    };

    const cancelEditStudent = () => {
        setEditingUsername(null);
        setEditDraft(null);
        setEditError('');
    };

    /** Saves the row being edited. Resolves true when it saved, false otherwise. */
    const saveEditStudent = async () => {
        if (!editingUsername || !editDraft) return false;
        setEditSaving(true);
        setEditError('');
        try {
            const res = await updateSectionStudentService(orgCode, enrollSectionCode, editingUsername, {
                first_name: editDraft.firstName,
                last_name: editDraft.lastName,
                // Both travel: `email` is the parent's, `student_email` the
                // learner's own. Sent even when blank — clearing a learner's
                // address is a legitimate edit, since not every child has one.
                email: editDraft.email,
                student_email: editDraft.studentEmail,
                year_of_birth: editDraft.yearOfBirth,
                gender: editDraft.gender,
                city: editDraft.city,
                country: editDraft.country,
                state: editDraft.state,
                postal_code: editDraft.postalCode,
            });
            const updated = res?.data?.student;
            if (!updated) {
                // invokeApi surfaces the backend message as a toast; keep the row
                // in edit mode so the correction is not lost.
                setEditError('Could not save. Please check the details and try again.');
                return;
            }
            // Patch the row in place from the server's echo rather than from the
            // draft: the server normalises (trimming, year validation), and
            // showing the draft would display something it did not store.
            setExistingStudents((rows) => rows.map((r) => (
                r.username === editingUsername
                    ? {
                        ...r,
                        firstName: updated.first_name || '',
                        lastName: updated.last_name || '',
                        email: updated.email || '',
                        studentEmail: updated.student_email || '',
                        yearOfBirth: updated.year_of_birth || '',
                        gender: updated.gender || '',
                        city: updated.city || '',
                        country: updated.country || '',
                        state: updated.state || '',
                        postalCode: updated.postal_code || '',
                    }
                    : r
            )));
            cancelEditStudent();
            return true;
        } catch (err) {
            setEditError('Could not save. Please check the details and try again.');
            return false;
        } finally {
            setEditSaving(false);
        }
    };

    /**
     * The Existing Students footer's "Save changes". A row open for editing is
     * saved first — its own Save sits at the far end of the wide table, often
     * scrolled out of view, so this button is the one people reach for. The
     * dialog closes only once that save succeeds, so a failed edit is not lost.
     */
    const saveExistingAndClose = async () => {
        if (editingUsername && editDraft) {
            const saved = await saveEditStudent();
            if (!saved) return;
        }
        onClose();
    };

    const loadExistingStudents = useCallback(async (page = 1, search = '', pageSize = existingPageSize) => {
        if (!orgCode || !enrollSectionCode) {
            setExistingError('No class selected.');
            return;
        }
        setExistingLoading(true);
        setExistingError('');
        try {
            // Per-class roster — SERVER-side paginated + searched, so only one
            // page of a (possibly multi-thousand) roster travels per request.
            // roster_meta (usernames + latest postal, for the username-prefix
            // derivation) is fetched once, on the first load.
            const res = await getSectionStudentsService(orgCode, enrollSectionCode, {
                page,
                limit: pageSize,
                search: String(search || '').trim(),
                with_meta: !rosterMeta,
            });
            if (res === false) {
                setExistingError('Could not load students. Please sign in again and retry.');
                return;
            }
            const raw = res?.data;
            const all = (raw?.students || []).map((s) => ({
                firstName: s.first_name || '',
                lastName: s.last_name || '',
                // `email` is the PARENT's address — what every consent pathway
                // mails. `student_email` is the learner's own, and may legitimately
                // be blank. This table used to show `email` under a "Learner Email"
                // heading, naming the parent's address as the child's.
                email: s.email || '',
                studentEmail: s.student_email || '',
                yearOfBirth: s.year_of_birth || '',
                gender: s.gender || '',
                city: s.city || '',
                country: s.country || '',
                state: s.state || '',
                username: s.username || '',
                postalCode: s.postal_code || '',
                createdAt: s.created_at || null,
            }));
            // Deploy-order safety: an older backend ignores page/limit/search and
            // returns the FULL roster (and echoes no `page`). Detect that and
            // paginate/filter client-side so the tab still works either way.
            const serverMode = raw?.page != null;
            let pageRows = all;
            let total = Number(raw?.total) || all.length;
            if (!serverMode) {
                const q = String(search || '').trim().toLowerCase();
                const filtered = q
                    ? all.filter((s) => [s.firstName, s.lastName, s.email, s.studentEmail]
                        .some((v) => String(v || '').toLowerCase().includes(q)))
                    : all;
                total = filtered.length;
                pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
            }
            setExistingStudents(pageRows);
            // A row being edited is meaningless once the page it lived on has
            // been replaced.
            setEditingUsername(null);
            setEditDraft(null);
            setExistingTotal(total);
            setExistingPage(page);
            // Derivation source: server meta when provided; otherwise (legacy
            // backend) the full list we just received. Set once per dialog open.
            if (raw?.roster_meta) {
                setRosterMeta({
                    usernames: raw.roster_meta.usernames || [],
                    latestPostal: raw.roster_meta.latest_postal_code || '',
                });
            } else if (!serverMode && !rosterMeta) {
                setRosterMeta({
                    usernames: sortNewestFirst(all)
                        .map((s) => String(s.username || '').trim())
                        .filter(Boolean),
                    latestPostal: (sortNewestFirst(all).find((s) => s.postalCode) || {}).postalCode || '',
                    students: all,
                });
            }
            setExistingLoaded(true);
        } catch (err) {
            setExistingError(err?.message || 'Failed to load students.');
        } finally {
            setExistingLoading(false);
        }
    }, [orgCode, enrollSectionCode, rosterMeta, existingPageSize]);

    const handleViewModeChange = (nextMode) => {
        setViewMode(nextMode);
        if (nextMode === 'existing' && !existingLoaded && !existingLoading) {
            loadExistingStudents();
        }
    };

    // Enroll mode: load the roster on open — the class's username prefix,
    // numbering offset and postal code are re-derived from it (they are not
    // persisted anywhere else).
    useEffect(() => {
        if (open && isEnrollMode && enrollSectionCode && !existingLoaded && !existingLoading) {
            loadExistingStudents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEnrollMode, enrollSectionCode, existingLoaded]);

    // Prefill the enroll form once the roster arrives. The class's PERSISTED
    // prefs (stored on the section, updated on every save/edit) win; the roster
    // is the fallback for legacy classes and supplies the numbering offset.
    // Only fills fields the user hasn't typed into, so manual input is never
    // overwritten.
    useEffect(() => {
        if (!open || !isEnrollMode || !rosterMeta) return;
        const persistedPrefix = String(enrollTarget?.usernamePrefix || '').trim();
        const persistedPostal = String(enrollTarget?.schoolPostalCode || '').trim();
        // Derivation runs on the WHOLE section (rosterMeta), never on the
        // paginated table page — usernames arrive newest-first from the server,
        // and deriveUsernamePattern's stable sort preserves that order for the
        // {username}-only rows (legacy full lists still carry createdAt).
        const derivationSource = rosterMeta.students
            || (rosterMeta.usernames || []).map((u) => ({ username: u }));
        const { prefix, maxN } = deriveUsernamePattern(derivationSource, persistedPrefix);
        // Postal: persisted value first, else the newest student's.
        const postal = persistedPostal || rosterMeta.latestPostal || '';
        setEnrollDerived({ prefix, maxN, postal });
        const rosterUsernames = (rosterMeta.usernames || [])
            .map((u) => String(u || '').trim())
            .filter(Boolean);
        setClassList((prev) => prev.map((c) => {
            const updated = { ...c, existingUsernames: rosterUsernames };
            if (prefix && !String(c.userPrefix || '').trim()) {
                updated.userPrefix = prefix;
                // The class's own prefix — no availability check needed (it is
                // "taken" by these very students), and numbering continues
                // after the highest existing number.
                updated.userPrefixValid = true;
                updated.userPrefixChecked = prefix;
                updated.usernameStartOffset = maxN;
            }
            if (postal && !String(c.schoolPostalCode || '').trim()) {
                updated.schoolPostalCode = postal;
            }
            return updated;
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEnrollMode, rosterMeta]);

    // Debounced SERVER-side search: retyping resets to page 1. Skipped until the
    // first load has happened (the search box only renders on that tab anyway).
    useEffect(() => {
        if (!isEnrollMode || !existingLoaded) return undefined;
        const t = setTimeout(() => {
            loadExistingStudents(1, existingSearch);
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingSearch]);

    // Rows are already filtered + paginated (server-side; client fallback for an
    // older backend lives in loadExistingStudents).
    const filteredExistingStudents = existingStudents;
    const existingTotalPages = Math.max(1, Math.ceil(existingTotal / existingPageSize));

    // Clear prefix error when switching tabs
    useEffect(() => {
        setPrefixError('');
    }, [activeTabId]);

    const handlePrefixBlur = useCallback(async (value) => {
        const prefix = String(value || '').trim();
        const tabId = activeTabId;
        if (!prefix) {
            setClassList((prev) =>
                prev.map((c) =>
                    c.id === tabId ? { ...c, userPrefixValid: false, userPrefixChecked: '' } : c,
                ),
            );
            return;
        }
        // The class's own (roster-derived) prefix is by definition "taken"
        // globally — by these very students. Accept it without the availability
        // check and keep numbering after the existing students.
        if (isEnrollMode && enrollDerived?.prefix &&
            prefix.toLowerCase() === enrollDerived.prefix.toLowerCase()) {
            setClassList((prev) =>
                prev.map((c) =>
                    c.id === tabId
                        ? { ...c, userPrefixValid: true, userPrefixChecked: prefix, usernameStartOffset: enrollDerived.maxN }
                        : c,
                ),
            );
            setPrefixError('');
            return;
        }
        setPrefixChecking(true);
        setPrefixError('');
        try {
            const res = await checkUsernamePrefixService(prefix);
            const exists = !!res?.data?.exists;
            setClassList((prev) =>
                prev.map((c) =>
                    c.id === tabId
                        ? { ...c, userPrefixValid: !exists, userPrefixChecked: prefix }
                        : c,
                ),
            );
            if (exists) {
                setPrefixError(`Prefix "${prefix}" is not available. Please choose a different prefix.`);
            }
        } catch {
            // silent — don't block the user if the check fails
        } finally {
            setPrefixChecking(false);
        }
    }, [activeTabId, isEnrollMode, enrollDerived]);

    const activeClass = classList.find(c => c.id === activeTabId) || classList[0];

    const updateClassField = (field, value) => {
        setClassList(prev => prev.map(c => {
            if (c.id !== activeTabId) return c;
            const updated = { ...c, [field]: value };
            if (field === 'userPrefix') {
                updated.userPrefixValid = false;
                updated.userPrefixChecked = '';
                // The numbering offset only applies while using the class's own
                // prefix; a brand-new prefix starts at 1 (no existing users).
                updated.usernameStartOffset =
                    enrollDerived?.prefix &&
                    String(value || '').trim().toLowerCase() === enrollDerived.prefix.toLowerCase()
                        ? enrollDerived.maxN
                        : 0;
            }
            if (field === 'studentsCount') {
                const n = parseInt(value) || 0;
                if (n > 0) updated.students = buildStudentsArray(n, c.students);
            }
            return updated;
        }));
    };

    const updateStudent = (idx, field, value) => {
        setClassList(prev => prev.map(c => {
            if (c.id !== activeTabId) return c;
            return {
                ...c,
                students: c.students.map((s, i) => i === idx ? { ...s, [field]: value } : { ...s }),
            };
        }));
    };

    const addRow = () => {
        setClassList(prev => prev.map(c => {
            if (c.id !== activeTabId) return c;
            const newStudents = [...c.students.map(s => ({ ...s })), { ...EMPTY_STUDENT }];
            return { ...c, students: newStudents, studentsCount: String(newStudents.length) };
        }));
    };

    const deleteRow = (idx) => {
        setClassList(prev => prev.map(c => {
            if (c.id !== activeTabId || c.students.length <= 1) return c;
            const newStudents = c.students.filter((_, i) => i !== idx);
            return { ...c, students: newStudents, studentsCount: String(newStudents.length) };
        }));
    };

    const addAnotherClass = () => {
        if (isEnrollMode) return;
        const newClass = createEmptyClass();
        setClassList(prev => [...prev, newClass]);
        setActiveTabId(newClass.id);
    };

    const removeClassTab = (id) => {
        if (isEnrollMode || classList.length <= 1) return;
        const next = classList.filter(c => c.id !== id);
        setClassList(next);
        if (id === activeTabId) setActiveTabId(next[0]?.id ?? null);
    };

    const setCsvFile = (file) => {
        if (!file) return;
        setClassList(prev => prev.map(c =>
            c.id === activeTabId ? { ...c, csvFile: file } : c
        ));
    };

    const handleCsvUpload = (e) => setCsvFile(e.target.files?.[0]);

    const handleCsvDrop = (e) => {
        e.preventDefault();
        setCsvFile(e.dataTransfer.files?.[0]);
    };

    /**
     * The school to PRE-FILL into a downloaded template:
     *  - edit (enroll) → the class's current school,
     *  - create + existing → the picked school,
     *  - create + new → what the user typed so far (mapped to institute_* shape).
     * Returns null → template uses the generic example row.
     */
    const resolvePrefillSchool = () => {
        let code = '';
        if (isEnrollMode) code = String(enrollTarget?.instituteCode || '').trim();
        else if (schoolMode === 'existing') code = String(selectedSchoolCode || '').trim();
        if (code && code !== 'default') {
            return (schools || []).find((s) => s.institute_code === code) || null;
        }
        if (!isEnrollMode && schoolMode === 'new') {
            const n = String(newSchool.schoolName || '').trim();
            if (!n) return null;
            return {
                institute_name: n,
                institute_head_email: newSchool.headEmail,
                institute_head_name: [newSchool.headFirstName, newSchool.headLastName].filter(Boolean).join(' ').trim(),
                institute_head_country: newSchool.headCountry,
                institute_head_city: newSchool.headCity,
                institute_head_postal_code: newSchool.headPostalCode,
            };
        }
        return null;
    };

    /** Validate the School picker and run the table save with the selection. */
    const handleSaveWithSchool = () => {
        const { school, error } = buildSchoolSelection();
        if (error) {
            toast(TOAST_TYPES.WARN, 'Classes', error);
            return;
        }
        if (networkSchools.length > 0 && !selectedNetworkOrgCode) {
            toast(TOAST_TYPES.WARN, 'Classes', 'Choose a school for this class.');
            return;
        }
        runSave(
            classList,
            (list) => onSave(list, school, selectedNetworkOrgCode || null),
            saveDisabled,
            prefixError,
            { enrollOnly: isEnrollMode },
        );
    };

    const handleCsvTabSave = () => {
        if (saveDisabled) {
            toast(TOAST_TYPES.INFO, 'Classes', 'Please wait for the current request to finish.');
            return;
        }
        if (prefixError) {
            toast(TOAST_TYPES.WARN, 'Classes', prefixError);
            return;
        }
        const { school, error: schoolError } = buildSchoolSelection();
        // A file is checked by the page: the network's classes template names each
        // class's school itself (School Email), so it needs no school picked here.
        if (networkSchools.length > 0 && !selectedNetworkOrgCode && !activeClass.csvFile) {
            toast(TOAST_TYPES.WARN, 'Classes', 'Choose a school for this class.');
            return;
        }
        if (typeof onLicenseCsvUpload === 'function' && activeClass.csvFile) {
            // File path: CSV mode has no School picker — the school comes ENTIRELY
            // from the uploaded file's own school block (create mode). In enroll
            // mode the parent wrapper adds forceSchool, locking it to the class's
            // own school regardless. So we never pass a picker school here.
            const sectionInfo = {
                section_code: String(activeClass.className || '').trim(),
                // The class details typed on "Enter manually" before switching to the
                // upload: used where the file's own class block is empty (LA-SW-05Oct-3).
                typedClass: {
                    grade_level: String(activeClass.gradeLevel || '').trim(),
                    academic_year: String(activeClass.academicYear || '').trim(),
                    incharge_first_name: String(activeClass.teacherFirstName || '').trim(),
                    incharge_last_name: String(activeClass.teacherLastName || '').trim(),
                    incharge_email: String(activeClass.teacherEmail || '').trim(),
                },
                license_count: Number(activeClass.studentsCount) || 0,
                school: null,
                // A network's own picker — the member school the file is for.
                networkSchoolKey: selectedNetworkOrgCode || null,
            };
            Promise.resolve(onLicenseCsvUpload(activeClass.csvFile, sectionInfo)).catch(() => {});
            return;
        }
        if (typeof onLicenseCsvUpload === 'function' && !activeClass.csvFile) {
            toast(TOAST_TYPES.WARN, 'Classes', 'Choose a file to upload.');
            return;
        }
        if (schoolError) {
            toast(TOAST_TYPES.WARN, 'Classes', schoolError);
            return;
        }
        runSave(classList, (list) => onSave(list, school), saveDisabled, prefixError, { enrollOnly: isEnrollMode });
    };

    if (!activeClass) return null;
    if (isEnrollMode && !enrollTarget) return null;

    // Network only — shown for manual entry and for an uploaded file alike.
    const networkSchoolPicker = networkSchools.length > 0 ? (
        <TextField
            select
            label="Select a school *"
            variant="outlined"
            size="small"
            value={selectedNetworkOrgCode}
            onChange={(e) => setSelectedNetworkOrgCode(e.target.value)}
            style={{ minWidth: 220, marginTop: 8 }}
        >
            {networkSchools.map((s) => (
                <MenuItem key={s.org_code} value={s.org_code}>{s.org_name}</MenuItem>
            ))}
        </TextField>
    ) : null;

    const prefixTrimmed = String(activeClass.userPrefix || '').trim();
    const canPreviewUsernames =
        prefixTrimmed &&
        activeClass.userPrefixValid &&
        activeClass.userPrefixChecked === prefixTrimmed;
    const usernameOffset = Number(activeClass.usernameStartOffset) || 0;
    // Precompute one username per row, continuing after the offset and SKIPPING
    // any that already exist in this class's roster — so a preview (and the saved
    // value) never reuses an existing learner's username. Mirrors the generation
    // in buildInsiderLicensePostDataFromClassTabs.
    const previewUsernames = (() => {
        if (!canPreviewUsernames) return [];
        const used = new Set(
            (activeClass.existingUsernames || []).map((u) => String(u || '').trim().toLowerCase()),
        );
        let n = usernameOffset + 1;
        return (activeClass.students || []).map(() => {
            while (used.has(`${prefixTrimmed}${n}`.toLowerCase())) n += 1;
            const u = `${prefixTrimmed}${n}`;
            used.add(u.toLowerCase());
            n += 1;
            return u;
        });
    })();
    const username = (idx) => (canPreviewUsernames ? previewUsernames[idx] : '—');
    const activePrefixNeedsCheck =
        !!prefixTrimmed && (!activeClass.userPrefixValid || activeClass.userPrefixChecked !== prefixTrimmed);

    // Hide the username column entirely when no student row has any data filled in
    const hasStudentData = (activeClass.students || []).some(isStudentRowFilled);
    const filledStudentRowCount = (activeClass.students || []).filter(isStudentRowFilled).length;
    // "N students total" across every class tab — counts actually-filled rows,
    // not just how many blank rows are showing.
    const totalStudentsAcrossTabs = (classList || []).reduce(
        (sum, c) => sum + (c.students || []).filter(isStudentRowFilled).length,
        0,
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: classes.studentsDialogPaper }}
            maxWidth="lg"
            fullWidth
            BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.4)' } }}
        >
            {/* ── Header ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '20px', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography className={classes.dialogTitle} style={{ marginBottom: 0, textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {isEnrollMode ? `Students — ${activeClass.className || enrollTarget?.className || 'Class'}` : 'Add a Class'}
                    </Typography>
                    <Typography style={{ color: '#aaa', fontSize: '13px', margin: 0 }}>
                        {isEnrollMode
                            ? 'Add students, or update the ones already enrolled.'
                            : 'Create the class and add its learners.'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} style={{ marginLeft: 12, flexShrink: 0 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* ── Enroll mode: switch between adding students and viewing this class's existing students ── */}
            {isEnrollMode && (
                <Box className={classes.studentTabsContainer} style={{ flexShrink: 0 }}>
                    <Box
                        className={`${classes.studentTab} ${viewMode === 'form' ? classes.studentTabActive : ''}`}
                        onClick={() => handleViewModeChange('form')}
                    >
                        Add Students
                    </Box>
                    <Box
                        className={`${classes.studentTab} ${viewMode === 'existing' ? classes.studentTabActive : ''}`}
                        onClick={() => handleViewModeChange('existing')}
                    >
                        {`Existing Students (${enrollTarget?.studentsDisplay ?? 0})`}
                    </Box>
                </Box>
            )}

            {/* ── Existing Students for this class (read-only) ── */}
            {isEnrollMode && viewMode === 'existing' && (
                <Box className={classes.studentsDialogScroll}>
                    <Box sx={{ mb: '12px' }}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={existingSearch}
                            onChange={(e) => setExistingSearch(e.target.value)}
                            placeholder="Search by name or email…"
                        />
                    </Box>

                    {existingLoading && (
                        <Typography style={{ fontSize: 13, color: '#9CA3AF', padding: '12px 0' }}>
                            Loading students…
                        </Typography>
                    )}
                    {!existingLoading && existingError && (
                        <Typography style={{ fontSize: 13, color: '#DC2626', padding: '12px 0' }}>
                            {existingError}
                        </Typography>
                    )}
                    {!existingLoading && !existingError && filteredExistingStudents.length === 0 && (
                        <Typography style={{ fontSize: 13, color: '#9CA3AF', padding: '12px 0' }}>
                            {existingSearch.trim()
                                ? 'No students match your search.'
                                : 'No students have been added to this class yet.'}
                        </Typography>
                    )}
                    {!existingLoading && !existingError && filteredExistingStudents.length > 0 && (
                        <Box className={classes.studentsTableContainer}>
                            {/* Same columns, in the same order, as the Add Students
                                table — so a learner reads back with every field they
                                were added with. Wide table: it scrolls sideways. */}
                            <table className={classes.studentsTableWide}>
                                <thead>
                                    <tr>
                                        <th className={classes.studentsThIndex}>#</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 150 }}>First Name</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 150 }}>Last Name</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 130 }}>Year of Birth</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 210 }}>Learner Email</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 210 }}>Parent Email</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 130 }}>Gender</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 150 }}>City</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 200 }}>Country</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 180 }}>State</th>
                                        <th className={classes.studentsTh} style={{ minWidth: 130 }}>Postal Code</th>
                                        <th className={classes.studentsTh}>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredExistingStudents.map((s, idx) => {
                                        const isEditing = editingUsername && s.username === editingUsername;
                                        const cell = (field, extra = {}) => (
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                value={editDraft?.[field] ?? ''}
                                                onChange={(e) => {
                                                    // Read now: React 16 reuses the event, so e.target is
                                                    // null inside the updater and the page went blank
                                                    // on the first key (LA-SNW,SW-19Oct-01).
                                                    const { value } = e.target;
                                                    setEditDraft((d) => ({ ...d, [field]: value }));
                                                }}
                                                disabled={editSaving}
                                                fullWidth
                                                {...extra}
                                            />
                                        );
                                        return (
                                            <tr key={s.username || `${s.email}-${idx}`}>
                                                {/* Continue numbering across pages (11, 12, … on page 2). */}
                                                <td className={classes.studentsTdIndex}>{(existingPage - 1) * existingPageSize + idx + 1}</td>
                                                {isEditing ? (
                                                    <>
                                                        <td className={classes.studentsTdText}>{cell('firstName')}</td>
                                                        <td className={classes.studentsTdText}>{cell('lastName')}</td>
                                                        <td className={classes.studentsTdText}>
                                                            {cell('yearOfBirth', { inputProps: { maxLength: 4, inputMode: 'numeric' } })}
                                                        </td>
                                                        <td className={classes.studentsTdText}>{cell('studentEmail', { type: 'email' })}</td>
                                                        <td className={classes.studentsTdText}>{cell('email', { type: 'email' })}</td>
                                                        <td className={classes.studentsTdText}>
                                                            {cell('gender', {
                                                                select: true,
                                                                SelectProps: { displayEmpty: true },
                                                                children: [
                                                                    <MenuItem key="" value="">Select</MenuItem>,
                                                                    <MenuItem key="Male" value="Male">Male</MenuItem>,
                                                                    <MenuItem key="Female" value="Female">Female</MenuItem>,
                                                                    <MenuItem key="Other" value="Other">Other</MenuItem>,
                                                                ],
                                                            })}
                                                        </td>
                                                        <td className={classes.studentsTdText}>{cell('city')}</td>
                                                        <td className={classes.studentsTdText}>
                                                            <Autocomplete
                                                                size="small"
                                                                options={getCountries()}
                                                                getOptionLabel={(o) => o.label || ''}
                                                                isOptionEqualToValue={(o, v) => o.code === v.code}
                                                                value={getCountries().find((c) => c.code === editDraft?.country) || null}
                                                                // A state belongs to one country, so a new
                                                                // country clears it — as the Add Students table does.
                                                                onChange={(e, v) => setEditDraft((d) => ({ ...d, country: v ? v.code : '', state: '' }))}
                                                                disabled={editSaving}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} placeholder="Select country" variant="outlined" />
                                                                )}
                                                            />
                                                        </td>
                                                        <td className={classes.studentsTdText}>
                                                            {(() => {
                                                                const statesForRow = getStatesByCountry(editDraft?.country);
                                                                // A state with no list for its country (or typed in a
                                                                // CSV) stays a free-text field so it can still be fixed.
                                                                if (!editDraft?.country || statesForRow.length === 0
                                                                    || (editDraft.state && !statesForRow.some((st) => st.code === editDraft.state))) {
                                                                    return cell('state', { placeholder: !editDraft?.country ? 'Select country first' : '' });
                                                                }
                                                                return (
                                                                    <Autocomplete
                                                                        size="small"
                                                                        options={statesForRow}
                                                                        getOptionLabel={(o) => o.name || ''}
                                                                        isOptionEqualToValue={(o, v) => o.code === v.code}
                                                                        value={statesForRow.find((st) => st.code === editDraft.state) || null}
                                                                        onChange={(e, v) => setEditDraft((d) => ({ ...d, state: v ? v.code : '' }))}
                                                                        disabled={editSaving}
                                                                        renderInput={(params) => (
                                                                            <TextField {...params} placeholder="Select state" variant="outlined" />
                                                                        )}
                                                                    />
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className={classes.studentsTdText}>{cell('postalCode')}</td>
                                                        <td className={classes.studentsTdText}>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    disabled={editSaving}
                                                                    onClick={saveEditStudent}
                                                                    sx={{ textTransform: 'none', minWidth: 60 }}
                                                                >
                                                                    {editSaving ? 'Saving…' : 'Save'}
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={editSaving}
                                                                    onClick={cancelEditStudent}
                                                                    sx={{ textTransform: 'none', minWidth: 60 }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </Box>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className={classes.studentsTdText}>{s.firstName || '—'}</td>
                                                        <td className={classes.studentsTdText}>{s.lastName || '—'}</td>
                                                        <td className={classes.studentsTdText}>{s.yearOfBirth || '—'}</td>
                                                        <td className={classes.studentsTdText}>{s.studentEmail || '—'}</td>
                                                        <td className={classes.studentsTdText}>{s.email || '—'}</td>
                                                        <td className={classes.studentsTdText}>{genderLabel(s.gender) || '—'}</td>
                                                        <td className={classes.studentsTdText}>{s.city || '—'}</td>
                                                        <td className={classes.studentsTdText}>{countryLabel(s.country) || '—'}</td>
                                                        <td className={classes.studentsTdText}>{stateLabel(s.country, s.state) || '—'}</td>
                                                        <td className={classes.studentsTdText}>{s.postalCode || '—'}</td>
                                                        <td className={classes.studentsTdText}>
                                                            {/* No delete: a student cannot be removed once
                                                                entered, so edit is the only action here. */}
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled={!s.username || Boolean(editingUsername)}
                                                                onClick={() => startEditStudent(s)}
                                                                sx={{ textTransform: 'none', minWidth: 60 }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                    )}
                    {editError && (
                        <Typography style={{ fontSize: 13, color: '#DC2626', paddingTop: 8 }}>
                            {editError}
                        </Typography>
                    )}

                    {!existingLoading && !existingError && filteredExistingStudents.length > 0 && (
                        <Typography style={{ fontSize: 12.5, color: '#9CA3AF', paddingTop: 8 }}>
                            Accounts can be corrected but not deleted — a license is consumed once an account is published.
                        </Typography>
                    )}

                    {/* ── Pagination (server-side): numbered pages, matching the
                        Manage Classes table — only the current page is highlighted. ── */}
                    {!existingError && existingTotal > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography style={{ fontSize: 12.5, color: '#6B7280' }}>
                                    Rows per page:
                                </Typography>
                                <TextField
                                    select
                                    size="small"
                                    variant="outlined"
                                    value={existingPageSize}
                                    onChange={(e) => {
                                        const n = Number(e.target.value);
                                        setExistingPageSize(n);
                                        loadExistingStudents(1, existingSearch, n);
                                    }}
                                    style={{ width: 76 }}
                                >
                                    {EXISTING_PAGE_SIZE_OPTIONS.map((n) => (
                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                    ))}
                                </TextField>
                                <Typography style={{ fontSize: 12.5, color: '#6B7280' }}>
                                    {`${existingTotal.toLocaleString()} student${existingTotal === 1 ? '' : 's'}`}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography style={{ fontSize: 12.5, color: '#6B7280' }}>
                                    {`Page ${existingPage} of ${existingTotalPages}`}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={existingLoading || existingPage <= 1}
                                    onClick={() => loadExistingStudents(existingPage - 1, existingSearch)}
                                    sx={paginationNavBtnSx}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={existingLoading || existingPage >= existingTotalPages}
                                    onClick={() => loadExistingStudents(existingPage + 1, existingSearch)}
                                    sx={paginationNavBtnSx}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {isEnrollMode && viewMode === 'existing' && (
                <Box className={classes.studentsDialogFooter}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                            {`${enrollTarget?.studentsDisplay ?? 0} students in this class`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    textTransform: 'none', fontWeight: 600, fontSize: '14.5px', borderRadius: '50px',
                                    color: 'var(--brand-text, #374151)', borderColor: 'var(--brand-border, #D1D5DB)',
                                    padding: '10px 24px',
                                }}
                            >
                                Cancel
                            </Button>
                            {/* Saves the row open for editing, if any, then closes —
                                see saveExistingAndClose. */}
                            <Button
                                className={classes.saveAllBtn}
                                onClick={saveExistingAndClose}
                                disabled={editSaving}
                            >
                                {editSaving ? 'Saving…' : 'Save changes'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {viewMode === 'form' && (
            <>
            <Box className={classes.studentsDialogScroll}>
            {/* ── Input Mode Toggle ── (top of the form: choose how to add
                students first — manual entry is selected by default). */}
            <Box className={classes.inputModeToggle}>
                <Box
                    className={`${classes.inputModeOption} ${activeClass.inputMode === 'table' ? classes.inputModeActive : ''}`}
                    onClick={() => updateClassField('inputMode', 'table')}
                >
                    <Box sx={{
                        width: 10, height: 10, borderRadius: '2px', mr: 1,
                        backgroundColor: activeClass.inputMode === 'table' ? '#fff' : 'var(--brand-text-muted, #9CA3AF)',
                    }} />
                    Add Manually
                </Box>
                {/* Open to a School Network too (bug sheet Ian-SNW-29Sep-9): the
                    file goes to the school picked in "Select a school" below. */}
                <Box
                    className={`${classes.inputModeOption} ${activeClass.inputMode === 'csv' ? classes.inputModeActive : ''}`}
                    onClick={() => updateClassField('inputMode', 'csv')}
                >
                    <UploadIcon style={{ fontSize: 16, marginRight: 6 }} />
                    Import CSV
                </Box>
            </Box>
            {/* ── School / Group: REMOVED for a single-school workspace ──────
                Add a Class does not ask a single School/Network Workspace which
                INSTITUTE (sub-campus) a class belongs to — the class belongs to
                the workspace it was created in.

                The mockups never had it. The Add-a-Class dialog is drawn on
                School Workspace slide 24 (SW-P0, P1A-My Classes-AddAccounts) and
                Network Workspace slides 40 and 42 (SNW-ALL / SNW-P0, P1A-My
                Classes-AddAccounts); all three go straight from the
                "Enter manually / Upload Excel" toggle to the class tabs. Neither
                deck, its speaker notes, its comments, nor the Navigation Bar
                sheet mentions a School/Group field anywhere.

                Confirmed by Ian (25 Aug 2026), asked directly whether it belongs
                to Networks, Single Schools or both:

                  "This should not appear for both. Schools should be added from
                   'My schools' not 'My classes'."

                So schools are created in My Schools, and a class simply belongs
                to the workspace it was created in. buildSchoolSelection above
                now always resolves to "no school", which is the path a
                single-school account already took.

                A School Network is a DIFFERENT question, addressed later and
                separately: a network has no single "workspace" org of its own
                to default to, so a class must say which member school it goes
                to — the "Which school?" picker rendered below, gated on
                `networkSchools`, is that answer. It has nothing to do with the
                INSTITUTE/sub-campus picker described above, which stays gone
                for every workspace type.                                    */}
            {/* Class tabs / "Add Another Class" are a MANUAL-entry concept only —
                a CSV file can carry multiple classes itself, so they're hidden in
                CSV mode (proper segregation of the two input methods). */}
            {!isEnrollMode && activeClass.inputMode === 'table' ? (
                <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {classList.map((cls, idx) => {
                            const isActive = activeTabId === cls.id;
                            return (
                                <Box
                                    key={cls.id}
                                    onClick={() => setActiveTabId(cls.id)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                                        borderRadius: '999px', padding: '7px 12px', fontSize: '13px', fontWeight: 700,
                                        border: isActive ? '1px solid #FF6900' : '1px solid var(--brand-border, #E5E7EB)',
                                        backgroundColor: isActive ? 'rgba(255,105,0,0.08)' : 'var(--brand-surface, #fff)',
                                        color: 'var(--brand-text, #111827)',
                                    }}
                                >
                                    <span>{cls.className || `Class ${idx + 1}`}</span>
                                    {String(cls.gradeLevel || '').trim() && (
                                        <span style={{ color: 'var(--brand-text-muted, #9CA3AF)', fontWeight: 500 }}>
                                            {cls.gradeLevel.replace(/^Grade\s*/i, 'Gr ')}
                                        </span>
                                    )}
                                    {classList.length > 1 && (
                                        <CloseIcon
                                            style={{ fontSize: 14, color: 'var(--brand-text-muted, #9CA3AF)' }}
                                            onClick={(e) => { e.stopPropagation(); removeClassTab(cls.id); }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                        <Box
                            onClick={addAnotherClass}
                            sx={{
                                display: 'flex', alignItems: 'center', cursor: 'pointer',
                                borderRadius: '999px', padding: '7px 12px', fontSize: '13px', fontWeight: 700,
                                border: '1px solid #FF6900', color: '#FF6900', backgroundColor: 'var(--brand-surface, #fff)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <AddIcon style={{ fontSize: 15, marginRight: 4 }} /> Add Another Class
                        </Box>
                    </Box>
                    <Typography style={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #9CA3AF)', whiteSpace: 'nowrap' }}>
                        {`${classList.length} class${classList.length === 1 ? '' : 'es'}, ${totalStudentsAcrossTabs} learner${totalStudentsAcrossTabs === 1 ? '' : 's'} total`}
                    </Typography>
                </Box>
                {/*
                  * Network only — a class must belong to one of the network's
                  * own member schools, and there is no single "workspace" org
                  * to default to. Required before Create Class is enabled.
                  * Spaced off the class chips — its floating label sat on them.
                  */}
                <Box sx={{ mt: 1.5 }}>{networkSchoolPicker}</Box>
                </Box>
            ) : null}
            {/* The same picker for an uploaded file: every class in the file
                goes to the school chosen here. */}
            {!isEnrollMode && activeClass.inputMode === 'csv' && networkSchools.length > 0 ? (
                <Box sx={{ mb: 2 }}>{networkSchoolPicker}</Box>
            ) : null}

            {/* ── Enroll mode: class summary + modules ── */}
            {/* Hidden per request — not in the new "Students — {className}" mockup.
                State/handler (linkedData/linkedLoading) still exists; just not
                reachable from this dialog for now. */}
            {false && isEnrollMode && enrollTarget && (
                <>

                    {/* Modules visible to this class — capped height so the student table stays reachable */}
                    <Box className={classes.enrollModulesPanel}>
                        <Typography style={{ fontSize: '13px', fontWeight: 700, color: 'var(--brand-text, #374151)', marginBottom: 8 }}>
                            Modules visible to this class
                        </Typography>
                        {linkedLoading && (
                            <Typography style={{ fontSize: '13px', color: 'var(--brand-text-muted, #9CA3AF)' }}>Loading linked modules…</Typography>
                        )}
                        {!linkedLoading && linkedData && (() => {
                            let hasAny = false;
                            const blocks = ENROLL_LINK_BUCKETS.map(({ key, label }) => {
                                const arr = linkedData[key];
                                if (!Array.isArray(arr) || arr.length === 0) return null;
                                hasAny = true;
                                return (
                                    <Box key={key} style={{ marginBottom: 6 }}>
                                        <Typography style={{ fontSize: '11px', fontWeight: 700, color: '#FF6900', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                                            {label}
                                        </Typography>
                                        {arr.map((m) => (
                                            <Typography key={m.module_code} style={{ fontSize: '13px', color: 'var(--brand-text, #374151)', marginBottom: 2 }}>
                                                {m.module_name || m.module_code}
                                                {' '}
                                                <span style={{ fontSize: '11px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                    ({formatModuleType(m.module_type)})
                                                </span>
                                            </Typography>
                                        ))}
                                    </Box>
                                );
                            });
                            if (!hasAny) {
                                return <Typography style={{ fontSize: '13px', color: 'var(--brand-text-muted, #9CA3AF)' }}>No modules linked yet.</Typography>;
                            }
                            return blocks;
                        })()}
                        {!linkedLoading && !linkedData && (
                            <Typography style={{ fontSize: '13px', color: 'var(--brand-text-muted, #9CA3AF)' }}>Could not load linked modules.</Typography>
                        )}
                    </Box>
                </>
            )}

            {/* ── Class Info ── (manual entry only, create mode only. Enroll mode
                ("Manage Students") no longer edits class details here — that's
                the standalone Edit Class dialog's job now.) */}
            {!isEnrollMode && activeClass.inputMode === 'table' && (
            <Box className={classes.classInfoSection}>
                {!isEnrollMode && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                            Class details
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                            {'Editing: '}
                            <Box component="span" sx={{ fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                {activeClass.className || `Class ${Math.max(0, classList.findIndex(c => c.id === activeTabId)) + 1}`}
                            </Box>
                        </Typography>
                    </Box>
                )}
                <Box className={classes.classInfoGrid} sx={{ mb: 1.5 }}>
                    <TextField
                        label="Class Name *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={activeClass.className}
                        onChange={(e) => updateClassField('className', e.target.value)}
                        placeholder="Class name"
                        inputProps={{ maxLength: MAX_SECTION_NAME_LEN, readOnly: isEnrollMode }}
                        disabled={isEnrollMode}
                    />
                    <TextField
                        label="Grade Level *"
                        select
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={activeClass.gradeLevel}
                        onChange={(e) => updateClassField('gradeLevel', e.target.value)}
                        helperText={
                            isEnrollMode && !String(activeClass.gradeLevel || '').trim()
                                ? 'Required when adding students.'
                                : ''
                        }
                    >
                        {GRADE_LEVEL_OPTIONS.map((g) => (
                            <MenuItem key={g} value={g}>{g}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Academic Year"
                        select
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={activeClass.academicYear}
                        onChange={(e) => updateClassField('academicYear', e.target.value)}
                    >
                        {ACADEMIC_YEAR_OPTIONS.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box className={classes.classInfoGrid}>
                    {/*
                      * Username Prefix: REMOVED.
                      *
                      * The Add a Class mockups do not have it — School deck slide 24
                      * and Network deck slides 40/42 go Class details -> Class Name,
                      * Grade Level, then straight to the teacher fields. It was an
                      * extra input this dialog carried on its own.
                      *
                      * The underlying username_prefix is untouched and still travels
                      * with the class: buildClassRows sends whatever value the class
                      * holds, the CSV upload path sets it from the file, and enroll
                      * mode still derives and verifies it. Only the manual input is
                      * gone, so nothing that consumed a prefix stops working.
                      */}
                    <TextField
                        label="Teacher First Name *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={activeClass.teacherFirstName}
                        onChange={(e) => updateClassField('teacherFirstName', e.target.value)}
                        placeholder="First name"
                        inputProps={{ maxLength: MAX_PERSON_NAME_LEN }}
                        error={!!nameError(activeClass.teacherFirstName)}
                        helperText={nameError(activeClass.teacherFirstName)}
                    />
                    <TextField
                        label="Teacher Last Name *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={activeClass.teacherLastName}
                        onChange={(e) => updateClassField('teacherLastName', e.target.value)}
                        placeholder="Last name"
                        inputProps={{ maxLength: MAX_PERSON_NAME_LEN }}
                        error={!!nameError(activeClass.teacherLastName)}
                        helperText={nameError(activeClass.teacherLastName)}
                    />
                    <TextField
                        label="Teacher Email *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={activeClass.teacherEmail}
                        onChange={(e) => updateClassField('teacherEmail', e.target.value)}
                        placeholder="Email address"
                        inputProps={{ maxLength: MAX_EMAIL_LEN }}
                        error={!!emailError(activeClass.teacherEmail)}
                        helperText={emailError(activeClass.teacherEmail)}
                    />
                </Box>
            </Box>
            )}

            {/* ── Class size — only for MANUAL entry (it drives the number of table
                rows), create mode only. In enroll mode ("Manage Students") the
                class already exists — students are simply added or edited
                directly in the table below, no size stepper needed. */}
            {!isEnrollMode && activeClass.inputMode === 'table' && (
            <Box sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                p: 2, mb: 2, backgroundColor: 'var(--brand-bg, #fafafa)',
            }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 1.5 }}>
                    Class size
                </Typography>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'var(--brand-text-muted, #6B7280)', mb: 0.5 }}>
                            # of Learners *
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                size="small"
                                disabled={activeClass.students.length <= 1}
                                onClick={() => updateClassField('studentsCount', String(activeClass.students.length - 1))}
                                sx={{ border: '1px solid var(--brand-border, #E5E7EB)' }}
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontSize: '18px', fontWeight: 700, minWidth: 24, textAlign: 'center' }}>
                                {activeClass.students.length}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => updateClassField('studentsCount', String(activeClass.students.length + 1))}
                                sx={{ border: '1px solid var(--brand-border, #E5E7EB)' }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                        <Typography sx={{ fontSize: '13px', color: 'var(--brand-text-muted, #6B7280)' }}>
                            Set the number of learners and the rows below follow. No licenses are used here — they are drawn from this school's allocation only when a program is linked.
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: 'var(--brand-text-muted, #9CA3AF)', mt: 0.5 }}>
                            {`${(activeClass.students || []).length} learner row${(activeClass.students || []).length === 1 ? '' : 's'} below, 0 licenses used so far`}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            )}

            {/* ── CSV Upload View ── */}
            {activeClass.inputMode === 'csv' && (
                <Box
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleCsvDrop}
                    sx={{
                        border: '2px dashed var(--brand-border, #D1D5DB)', borderRadius: '16px',
                        backgroundColor: 'var(--brand-bg, #FFFBF7)', textAlign: 'center',
                        padding: '40px 24px', mb: 2,
                    }}
                >
                    {/* Ian-SNW, SW-15Oct-01: the same words as Add a School's import. */}
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 1 }}>
                        {activeClass.csvFile ? `Selected: ${activeClass.csvFile.name}` : 'Set up your CSV file'}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: 'var(--brand-text-muted, #6B7280)', maxWidth: 640, mx: 'auto', mb: 2.5, lineHeight: 1.6 }}>
                        Follow the instructions in the template to add your classes. Imported entries are saved as drafts, and their learning spaces aren’t activated yet.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {/* Download Template first, then Upload File — same order as the schools upload. */}
                        <Button
                            onClick={() =>
                                onDownloadLicenseTemplate &&
                                Promise.resolve(onDownloadLicenseTemplate(activeClass?.className, resolvePrefillSchool(), isEnrollMode)).catch(() => {})
                            }
                            disabled={!onDownloadLicenseTemplate || !!saveDisabled}
                            variant="outlined"
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '14px', borderRadius: '50px',
                                color: 'var(--brand-text, #374151)', borderColor: 'var(--brand-border, #D1D5DB)',
                                padding: '10px 26px', backgroundColor: 'var(--brand-surface, #fff)',
                            }}
                        >
                            Download Template
                        </Button>
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '14px', borderRadius: '50px',
                                backgroundColor: '#FF6900', color: '#fff', padding: '10px 26px',
                                '&:hover': { backgroundColor: '#e55e00' },
                            }}
                        >
                            {activeClass.csvFile ? 'Upload Another File' : 'Upload File'}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            style={{ display: 'none' }}
                            onChange={handleCsvUpload}
                        />
                    </Box>
                </Box>
            )}

            {/* ── Add learner details ── (manual entry only, create mode only).
                Optional — the class saves fine with studentEntryMode: 'later' and
                zero roster rows; learners can be added from the class's own page
                anytime after creation. Enroll mode ("Manage Students") always
                goes straight to the table below — there's no "later" there since
                the class already exists. */}
            {!isEnrollMode && activeClass.inputMode === 'table' && (
            <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                        Add learner details
                    </Typography>
                    <Box sx={{
                        fontSize: '11.5px', fontWeight: 600, color: 'var(--brand-text-muted, #9CA3AF)',
                        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '999px', px: 1.5, py: 0.4,
                    }}>
                        Optional — you can fill these in later
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: activeClass.studentEntryMode === 'now' ? 1.5 : 0 }}>
                    {[
                        { value: 'now', title: 'Add now', desc: 'Type learner details below.' },
                        { value: 'later', title: 'Add later', desc: 'Create the class now; add learners anytime from its page.' },
                    ].map((opt) => {
                        const selected = activeClass.studentEntryMode === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                onClick={() => updateClassField('studentEntryMode', opt.value)}
                                sx={{
                                    flex: 1, minWidth: 220, cursor: 'pointer', position: 'relative',
                                    border: selected ? '1.5px solid #FF6900' : '1px solid var(--brand-border, #E5E7EB)',
                                    backgroundColor: selected ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #fff)',
                                    borderRadius: '12px', p: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                        {opt.title}
                                    </Typography>
                                    <Box sx={{
                                        width: 16, height: 16, borderRadius: '50%', flexShrink: 0, mt: 0.25,
                                        border: selected ? '5px solid #FF6900' : '1.5px solid var(--brand-border, #D1D5DB)',
                                        backgroundColor: '#fff',
                                    }} />
                                </Box>
                                <Typography sx={{ fontSize: '12px', color: 'var(--brand-text-muted, #6B7280)', mt: 0.5 }}>
                                    {opt.desc}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            )}

            {/* ── Manual Table View ── */}
            {activeClass.inputMode === 'table' && activeClass.studentEntryMode === 'now' && (
                <Box className={classes.studentsTableContainer} style={{ marginBottom: 0 }}>
                    <table className={classes.studentsTableWide}>
                        <thead>
                            <tr>
                                <th className={classes.studentsThIndex}>#</th>
                                {hasStudentData && <th className={classes.studentsThUsername}>Username</th>}
                                <th className={classes.studentsTh} style={{ minWidth: 150 }}>First Name *</th>
                                <th className={classes.studentsTh} style={{ minWidth: 150 }}>Last Name *</th>
                                <th className={classes.studentsTh} style={{ minWidth: 130 }}>Year of Birth *</th>
                                <th className={classes.studentsTh} style={{ minWidth: 210 }}>Learner Email</th>
                                <th className={classes.studentsTh} style={{ minWidth: 210 }}>Parent Email *</th>
                                <th className={classes.studentsTh} style={{ minWidth: 160 }}>Gender</th>
                                <th className={classes.studentsTh} style={{ minWidth: 150 }}>City</th>
                                <th className={classes.studentsTh} style={{ minWidth: 200 }}>Country</th>
                                <th className={classes.studentsTh} style={{ minWidth: 180 }}>State</th>
                                <th className={classes.studentsTh} style={{ minWidth: 130 }}>Postal Code</th>
                                <th className={classes.studentsThAction} />
                            </tr>
                        </thead>
                        <tbody>
                            {activeClass.students.map((row, idx) => (
                                <tr key={idx}>
                                    <td className={classes.studentsTdIndex}>{idx + 1}</td>
                                    {hasStudentData && (
                                        <td className={classes.studentsTdUsername}>
                                            {canPreviewUsernames ? (
                                                <Tooltip title="Preview — add student email so they receive login details" placement="top" arrow>
                                                    <span style={{ cursor: 'help', borderBottom: '1px dashed #9CA3AF' }}>
                                                        {username(idx)}
                                                    </span>
                                                </Tooltip>
                                            ) : username(idx)}
                                        </td>
                                    )}
                                    {/* Inline validation: the field turns red with a
                                        hint underneath as soon as the value is wrong,
                                        instead of the user finding out on submit. */}
                                    <td className={classes.studentsTd}>
                                        <input
                                            className={classes.studentsInput}
                                            style={invalidInputSx(!!nameError(row.firstName))}
                                            value={row.firstName}
                                            onChange={(e) => updateStudent(idx, 'firstName', e.target.value)}
                                            placeholder="e.g. John"
                                        />
                                        {nameError(row.firstName) && (
                                            <div style={{ color: '#DC2626', fontSize: 11, marginTop: 2 }}>{nameError(row.firstName)}</div>
                                        )}
                                    </td>
                                    <td className={classes.studentsTd}>
                                        <input
                                            className={classes.studentsInput}
                                            style={invalidInputSx(!!nameError(row.lastName))}
                                            value={row.lastName}
                                            onChange={(e) => updateStudent(idx, 'lastName', e.target.value)}
                                            placeholder="Doe"
                                        />
                                        {nameError(row.lastName) && (
                                            <div style={{ color: '#DC2626', fontSize: 11, marginTop: 2 }}>{nameError(row.lastName)}</div>
                                        )}
                                    </td>
                                    <td className={classes.studentsTd}>
                                        <select className={classes.studentsInput} value={row.yearOfBirth} onChange={(e) => updateStudent(idx, 'yearOfBirth', e.target.value)}>
                                            <option value="">Select</option>
                                            {BIRTH_YEAR_OPTIONS.map((y) => (
                                                <option key={y} value={y}>{y}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className={classes.studentsTd}>
                                        <input
                                            className={classes.studentsInput}
                                            style={invalidInputSx(!!emailError(row.studentEmail))}
                                            value={row.studentEmail}
                                            onChange={(e) => updateStudent(idx, 'studentEmail', e.target.value)}
                                            placeholder="learner@school.com"
                                        />
                                        {emailError(row.studentEmail) && (
                                            <div style={{ color: '#DC2626', fontSize: 11, marginTop: 2 }}>{emailError(row.studentEmail)}</div>
                                        )}
                                    </td>
                                    <td className={classes.studentsTd}>
                                        <input
                                            className={classes.studentsInput}
                                            style={invalidInputSx(isStudentRowFilled(row) && !!requiredEmailError(row.parentEmail))}
                                            value={row.parentEmail}
                                            onChange={(e) => updateStudent(idx, 'parentEmail', e.target.value)}
                                            placeholder="parent@example.com"
                                        />
                                        {isStudentRowFilled(row) && requiredEmailError(row.parentEmail) && (
                                            <div style={{ color: '#DC2626', fontSize: 11, marginTop: 2 }}>{requiredEmailError(row.parentEmail)}</div>
                                        )}
                                    </td>
                                    <td className={classes.studentsTd} style={{ minWidth: 160 }}>
                                        <select className={classes.studentsInput} value={row.gender} onChange={(e) => updateStudent(idx, 'gender', e.target.value)}>
                                            <option value="">Select</option>
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </td>
                                    <td className={classes.studentsTd} style={{ minWidth: 150 }}>
                                        <input
                                            className={classes.studentsInput}
                                            value={row.city || ''}
                                            onChange={(e) => updateStudent(idx, 'city', e.target.value)}
                                            placeholder="e.g. Singapore"
                                        />
                                    </td>
                                    <td className={classes.studentsTd} style={{ minWidth: 200 }}>
                                        <Autocomplete
                                            size="small"
                                            options={getCountries()}
                                            getOptionLabel={(o) => o.label || ''}
                                            isOptionEqualToValue={(o, v) => o.code === v.code}
                                            value={getCountries().find((c) => c.code === row.country) || null}
                                            onChange={(e, newValue) => {
                                                // A state/province belongs to one country, so
                                                // changing the country clears whatever state was
                                                // picked for the old one rather than leaving a
                                                // mismatched value in place.
                                                updateStudent(idx, 'country', newValue ? newValue.code : '');
                                                updateStudent(idx, 'state', '');
                                            }}
                                            sx={{ width: '100%' }}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder="Select country" variant="outlined" sx={autocompleteFieldSx} />
                                            )}
                                        />
                                    </td>
                                    <td className={classes.studentsTd} style={{ minWidth: 180 }}>
                                        {(() => {
                                            const statesForRow = getStatesByCountry(row.country);
                                            const disabled = !row.country || statesForRow.length === 0;
                                            return (
                                                <Autocomplete
                                                    size="small"
                                                    options={statesForRow}
                                                    getOptionLabel={(o) => o.name || ''}
                                                    isOptionEqualToValue={(o, v) => o.code === v.code}
                                                    value={statesForRow.find((s) => s.code === row.state) || null}
                                                    onChange={(e, newValue) => updateStudent(idx, 'state', newValue ? newValue.code : '')}
                                                    disabled={disabled}
                                                    sx={{ width: '100%' }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder={!row.country ? 'Select country first' : statesForRow.length === 0 ? 'N/A' : 'Select state'}
                                                            variant="outlined"
                                                            sx={autocompleteFieldSx}
                                                        />
                                                    )}
                                                />
                                            );
                                        })()}
                                    </td>
                                    <td className={classes.studentsTd}>
                                        <input
                                            className={classes.studentsInput}
                                            value={row.postalCode}
                                            onChange={(e) => updateStudent(idx, 'postalCode', e.target.value)}
                                            placeholder="e.g. 90001"
                                        />
                                    </td>
                                    <td className={classes.studentsTdAction}>
                                        <IconButton size="small" onClick={() => deleteRow(idx)} className={classes.studentsDeleteBtn} disabled={activeClass.students.length === 1}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            )}
            {activeClass.inputMode === 'table' && activeClass.studentEntryMode === 'now' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, mb: 2 }}>
                    <Button className={classes.addRowBtn} startIcon={<AddIcon />} onClick={addRow}>
                        Add Row
                    </Button>
                    <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                        {isEnrollMode
                            ? `${filledStudentRowCount} new student${filledStudentRowCount === 1 ? '' : 's'} ready to add`
                            : `${filledStudentRowCount} of ${activeClass.students.length} rows filled in`}
                    </Typography>
                </Box>
            )}

            </Box>

            {/* Batched-upload progress — the dialog stays open (onClose is blocked
                by the parent) until every part is uploaded and the list reloads. */}
            {uploadProgress && (
                <Box sx={{ px: 0, pt: 1.5, pb: 0.5 }}>
                    <Typography style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                        {`Uploading students… ${uploadProgress.done.toLocaleString()} / ${uploadProgress.total.toLocaleString()} — please keep this window open.`}
                    </Typography>
                    <Box style={{ height: 8, borderRadius: 4, background: 'var(--brand-surface, #E5E7EB)', overflow: 'hidden' }}>
                        <Box
                            style={{
                                height: '100%',
                                width: `${Math.round((uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100)}%`,
                                background: '#FF6900',
                                borderRadius: 4,
                                transition: 'width .4s ease',
                            }}
                        />
                    </Box>
                </Box>
            )}

            {activeClass.inputMode === 'csv' && (
                <Box className={classes.studentsDialogFooter}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                textTransform: 'none', fontWeight: 600, fontSize: '14.5px', borderRadius: '50px',
                                color: 'var(--brand-text, #374151)', borderColor: 'var(--brand-border, #D1D5DB)',
                                padding: '10px 24px',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={classes.saveAllBtn}
                            onClick={handleCsvTabSave}
                            disabled={
                                !!saveDisabled || !!prefixError || prefixChecking || activePrefixNeedsCheck
                                || (networkSchools.length > 0 && !selectedNetworkOrgCode && !activeClass.csvFile)
                            }
                        >
                            {isEnrollMode ? (uploadProgress ? 'Uploading…' : 'Upload Students') : (uploadProgress ? 'Uploading…' : 'Import')}
                        </Button>
                    </Box>
                </Box>
            )}

            {activeClass.inputMode === 'table' && (
                <Box className={classes.studentsDialogFooter}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {!isEnrollMode ? (
                            <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                {`${classList.length} class${classList.length === 1 ? '' : 'es'}, ${totalStudentsAcrossTabs} learner${totalStudentsAcrossTabs === 1 ? '' : 's'} total`}
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                {`${enrollTarget?.studentsDisplay ?? 0} students in this class`}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                sx={{
                                    textTransform: 'none', fontWeight: 600, fontSize: '14.5px', borderRadius: '50px',
                                    color: 'var(--brand-text, #374151)', borderColor: 'var(--brand-border, #D1D5DB)',
                                    padding: '10px 24px',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                className={classes.saveAllBtn}
                                onClick={handleSaveWithSchool}
                                disabled={
                                    !!saveDisabled || !!prefixError || prefixChecking || activePrefixNeedsCheck
                                    || (networkSchools.length > 0 && !selectedNetworkOrgCode)
                                }
                            >
                                {isEnrollMode
                                    ? 'Add students'
                                    : `Create ${classList.length} Class${classList.length === 1 ? '' : 'es'}`}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
            </>
            )}
        </Dialog>
    );
};

export default AddClassStudentsDialog;
